/**
 * Aspect Admin — Team members, roles, and permission matrix.
 */

import { icon } from "./icons.js";
import { renderShell, pageHead, statusBadge, openModal, toast, initials, closeOverlay, dropdownHtml, bindDropdowns } from "./shell.js";
import { escapeHtml } from "./auth_shell.js";
import { getSession } from "../auth_store.js";
import { navigate } from "../router.js";
import { t } from "../i18n.js";
import { TEAM, ROLES, PERMISSIONS } from "../mock_data.js";

let state = { tab: "members" };

const ROLE_LABELS = { Owner: "Owner", Admin: "Admin", Editor: "Editor", Viewer: "Viewer" };

function roleLabel(r) {
  // Owner/Admin/Editor/Viewer map to dictionary entries
  return t(r === "Admin" ? "Administrator" : ROLE_LABELS[r] ?? r);
}

function memberRow(m) {
  return `
    <tr data-row data-member="${m.id}">
      <td>
        <div class="ap-entity">
          <div class="ap-avatar ap-avatar--round">${initials(m.name)}</div>
          <div>
            <span class="ap-cell-main">${escapeHtml(m.name)}</span>
            <span class="ap-cell-sub">${escapeHtml(m.email)}</span>
          </div>
        </div>
      </td>
      <td>
        ${dropdownHtml({
          value: m.role,
          options: ROLES.map((r) => ({ value: r, label: roleLabel(r) })),
          disabled: m.role === "Owner",
          className: "ap-dropdown--sm",
        })}
      </td>
      <td>${statusBadge(m.status)}</td>
      <td>${m.twofa ? `<span class="ap-badge ap-badge--ok">${icon("shield", 11)} 2FA</span>` : `<span class="ap-badge ap-badge--warn">${t("No 2FA")}</span>`}</td>
      <td>${t(m.last)}</td>
      <td class="ap-cell-actions">
        ${m.status === "invited" ? `<button type="button" class="ap-btn ap-btn--soft ap-btn--sm" data-act="resend">${t("Resend")}</button>` : ""}
        ${m.role !== "Owner" ? `<button type="button" class="ap-iconbtn ap-iconbtn--bare" data-act="remove" title="${t("Remove member")}">${icon("trash", 15)}</button>` : ""}
      </td>
    </tr>
  `;
}

function matrixHtml() {
  return `
    <div class="ap-tablewrap ap-scroll">
      <table class="ap-table ap-matrix">
        <thead>
          <tr>
            <th>${t("Permission")}</th>
            ${ROLES.map((r) => `<th>${roleLabel(r)}</th>`).join("")}
          </tr>
        </thead>
        <tbody>
          ${PERMISSIONS.map(
            (p) => `
            <tr>
              <td class="ap-cell-main" style="font-weight:550;">${t(p.name)}</td>
              ${p.grants
                .map(
                  (g) => `
                <td>
                  <span class="ap-matrix__cell ${g ? "ap-matrix__cell--on" : "ap-matrix__cell--off"}">
                    ${g ? icon("check", 13) : icon("minus", 13)}
                  </span>
                </td>`
                )
                .join("")}
            </tr>`
          ).join("")}
        </tbody>
      </table>
    </div>
    <div class="ap-panel__foot">
      <span>${t("Custom roles are available on the Enterprise plan")}</span>
      <button type="button" class="ap-btn ap-btn--ghost ap-btn--sm" id="tm-contact">${t("Talk to us")} ${icon("chevronRight", 12)}</button>
    </div>
  `;
}

function inviteModal(root) {
  const panel = openModal(root, {
    title: t("Invite team member"),
    bodyHtml: `
      <div class="ap-field">
        <label class="ap-field__label">${t("Email addresses")}</label>
        <input class="ap-input" type="text" placeholder="${t("name@company.com, another@company.com")}" />
        <span class="ap-field__hint">${t("Separate multiple addresses with commas. 5 seats remaining on your plan.")}</span>
      </div>
      <div class="ap-field">
        <label class="ap-field__label">${t("Role")}</label>
        ${dropdownHtml({
          value: "Editor",
          options: [
            { value: "Editor", label: t("Editor — create spaces, run AI jobs") },
            { value: "Admin", label: t("Admin — manage clients, keys, and members") },
            { value: "Viewer", label: t("Viewer — read-only access") },
          ],
        })}
      </div>
      <div class="ap-field">
        <label class="ap-field__label">${t("Personal message")} <span style="font-weight:400;color:var(--ap-mist);">${t("(optional)")}</span></label>
        <textarea class="ap-textarea" placeholder="${t("Welcome to our Aspect workspace…")}"></textarea>
      </div>
    `,
    footHtml: `
      <button type="button" class="ap-btn ap-btn--ghost" data-mclose>${t("Cancel")}</button>
      <button type="button" class="ap-btn ap-btn--primary" data-msend>${icon("send", 15)} ${t("Send invitations")}</button>
    `,
  });
  panel?.querySelector("[data-mclose]")?.addEventListener("click", () => closeOverlay(root));
  panel?.querySelector("[data-msend]")?.addEventListener("click", () => {
    closeOverlay(root);
    toast(root, t("Invitations sent"), "ok");
  });
  bindDropdowns(panel);
}

export function renderTeam(root) {
  const session = getSession();
  if (!session) { navigate("/login", true); return; }

  const content = `
    ${pageHead(t("Team"), t("{a} active members · {p} pending invite · 10 seats on Scale plan", { a: TEAM.filter((m) => m.status === "active").length, p: TEAM.filter((m) => m.status === "invited").length }), `
      <button type="button" class="ap-btn ap-btn--primary" id="tm-invite">${icon("plus", 15)} ${t("Invite member")}</button>
    `)}

    <div class="ap-toolbar">
      <div class="ap-seg">
        <button type="button" class="ap-seg__btn ${state.tab === "members" ? "ap-seg__btn--on" : ""}" data-tab="members">${icon("users", 14)} ${t("Members")}</button>
        <button type="button" class="ap-seg__btn ${state.tab === "roles" ? "ap-seg__btn--on" : ""}" data-tab="roles">${icon("lock", 14)} ${t("Roles & permissions")}</button>
      </div>
    </div>

    <div class="ap-panel">
      ${
        state.tab === "members"
          ? `<div class="ap-tablewrap ap-scroll">
              <table class="ap-table">
                <thead><tr><th>${t("Member")}</th><th>${t("Role")}</th><th>${t("Status")}</th><th>${t("Security")}</th><th>${t("Last active")}</th><th></th></tr></thead>
                <tbody>${TEAM.map(memberRow).join("")}</tbody>
              </table>
            </div>
            <div class="ap-panel__foot">
              <span>${t("Role changes apply immediately and are recorded in the audit log")}</span>
              <a class="ap-btn ap-btn--ghost ap-btn--sm" href="#/audit">${t("View audit log")}</a>
            </div>`
          : matrixHtml()
      }
    </div>
  `;

  const el = renderShell(root, "/team", content);
  if (!el) return;

  el.querySelector("#tm-invite")?.addEventListener("click", () => inviteModal(root));
  el.querySelector("#tm-contact")?.addEventListener("click", () => toast(root, t("We'll reach out about Enterprise roles"), "info"));

  el.querySelectorAll("[data-tab]").forEach((tab) =>
    tab.addEventListener("click", () => {
      state = { tab: tab.dataset.tab };
      renderTeam(root);
    })
  );

  bindDropdowns(el, {
    onChange: ({ wrap, value }) => {
      if (wrap.closest("[data-member]")) toast(root, t("Role updated to {r}", { r: roleLabel(value) }), "ok");
    },
  });

  el.querySelector("tbody")?.addEventListener("click", (e) => {
    const act = e.target.closest("[data-act]")?.dataset?.act;
    const row = e.target.closest("[data-member]");
    if (!row || !act) return;
    const m = TEAM.find((x) => x.id === row.dataset.member);
    if (act === "resend") toast(root, t("Invitation resent to {e}", { e: m?.email }), "ok");
    if (act === "remove") toast(root, t("{n} removed from workspace", { n: m?.name }), "info");
  });
}
